import {
  loadStudySet,
  submitReview,
  suspendCard,
  undoReview,
  unsuspendCard,
  updateEnrollment,
} from "../lib/store";
import { buildQueue, grade, previewIntervals, rollback, stateKey, type Answer } from "../lib/srs";
import { formatInterval, guessTimeZone } from "../lib/time";
import { playCard } from "../lib/pronounce";
import { lang, num, t } from "../lib/i18n";
import { h, mount } from "../ui/dom";
import { navigate } from "../ui/router";
import { signedIn } from "../ui/app";
import { bidiText, spinner } from "./parts";
import type { Direction, QueueItem } from "../lib/types";
import type { Card as FsrsCard, ReviewLog } from "ts-fsrs";

/**
 * Enough to reverse the last action, locally and on the server.
 * A union because retiring a card is not a review and must be undone differently
 * — there is no log row to delete.
 */
type UndoPoint =
  | { kind: "review"; item: QueueItem; card: FsrsCard; log: ReviewLog }
  | { kind: "retire"; item: QueueItem };

export async function renderStudy(rootId: string, host: HTMLElement): Promise<void> {
  if (!signedIn) {
    navigate("/signin");
    return;
  }

  mount(host, h("div", { class: "screen" }, spinner()));

  const tz = guessTimeZone();
  const set = await loadStudySet(rootId, tz);

  /** Extra new-card allowance granted this session by "Keep going". */
  let extraNew = 0;
  /** `cardId:direction` handled this session, so a rebuild can't re-serve them. */
  const done = new Set<string>();

  /** Which side is the question. 0 = English first, 1 = Persian first. */
  const currentDirection = (): Direction => set.enrollment.directions[0] ?? 0;

  const itemKey = (item: QueueItem) => stateKey(item.card.id, item.direction);

  /**
   * The queue is rebuilt rather than fixed at session start, because raising the
   * daily allowance has to admit cards that were filtered out originally.
   * `set.states` is updated after every review so rebuilds see current scheduling.
   */
  function rebuild(): QueueItem[] {
    return buildQueue({
      cards: set.cards,
      states: set.states,
      enrollment: { ...set.enrollment, new_per_day: set.enrollment.new_per_day + extraNew },
      introducedToday: set.introducedToday,
      tz,
    }).filter((item) => !done.has(itemKey(item)));
  }

  /**
   * Choose which side is the question, and rebuild.
   *
   * This is NOT a display flip. FSRS state is keyed by direction because
   * recognition and production are different skills with different difficulty
   * curves — showing the Persian first while grading against the English-first
   * state would feed production attempts into the recognition schedule, and
   * production is the harder of the two, so it would drag those intervals down
   * for a skill that was never tested. Switching therefore changes the queue,
   * and every grade lands on that direction's own row.
   */
  function setDirection(next: Direction): void {
    if (busy || next === currentDirection()) return;

    set.enrollment = { ...set.enrollment, directions: [next] };
    queue = rebuild();
    index = 0;
    revealed = false;
    hinted = false;
    // The undo point indexes into the queue we just discarded.
    undoPoint = null;
    draw();

    // Remembered on the ROOT, where the enrollment row lives, so the choice
    // holds next session. Deliberately not awaited and its failure is not
    // surfaced: the session is already correct either way, and interrupting
    // someone mid-study to report that a preference didn't save would cost more
    // than the preference is worth.
    void updateEnrollment(set.tree.root.root_id, { directions: [next] }).catch(() => {});
  }

  /** New cards held back by the daily cap — what "Keep going" would unlock. */
  function heldBack(): number {
    const directions = set.enrollment.directions.length
      ? set.enrollment.directions
      : [0 as Direction];
    const counted = new Set<string>();
    let total = 0;
    for (const card of set.cards) {
      for (const direction of directions) {
        const key = stateKey(card.id, direction);
        if (done.has(key) || counted.has(card.id)) continue;
        const state = set.states.get(key);
        if (state?.suspended) continue;
        if (!state) {
          total++;
          counted.add(card.id);
        }
      }
    }
    return Math.max(0, total - queue.filter((item) => item.state === null).length);
  }

  let queue = rebuild();
  let index = 0;
  let reviewed = 0;
  let revealed = false;
  /** Hint shown for the CURRENT card only; reset whenever the card changes. */
  let hinted = false;
  let shownAt = Date.now();
  let undoPoint: UndoPoint | null = null;
  let busy = false;

  const screen = h("div", { class: "screen study" });
  mount(host, screen);

  function finish(): void {
    const more = heldBack();

    mount(
      screen,
      h("div", { class: "stack center" },
        h("h2", { text: t("sessionDone") }),
        // Two different endings: genuinely nothing left, versus stopped by the
        // daily cap. Telling someone "come back later" when they are willing to
        // keep working is the wrong answer — the cap exists to stop people being
        // buried, not to stop them learning.
        h("p", { class: "muted", text: more ? t("keepGoingBody") : t("sessionDoneBody") }),
        reviewed ? h("p", { class: "muted", text: t("reviewedCount", { n: reviewed }) }) : null,
        more
          ? h("button", {
              class: "primary",
              text: `${t("keepGoing")} · ${t("moreAvailable", { n: more })}`,
              on: {
                click: () => {
                  // One extra batch at a time, not all of them: unlocking 400 cards
                  // in one tap just recreates the problem the cap solves.
                  extraNew += Math.min(10, more);
                  queue = rebuild();
                  index = 0;
                  revealed = false;
                  hinted = false;
                  draw();
                },
              },
            })
          : null,
        h("button", {
          class: more ? "ghost" : "primary",
          text: t("navHome"),
          on: { click: () => navigate("/") },
        }),
      ),
    );
  }

  async function answer(item: QueueItem, choice: Answer): Promise<void> {
    if (busy) return; // guard against a double-tap double-submitting
    busy = true;
    try {
      const result = grade(item.state, choice, new Date());
      const saved = await submitReview(
        item.card.id, item.direction, result.rating, result.card, Date.now() - shownAt,
      );
      // Use the row the server returned rather than reconstructing one locally:
      // it is authoritative, and it carries server-side effects the client cannot
      // know about — the leech guard flipping `suspended`, for instance.
      // Keeping it current means a rebuild after "Keep going" sees this card as
      // reviewed instead of re-serving it as new.
      set.states.set(itemKey(item), saved);
      done.add(itemKey(item));

      undoPoint = { kind: "review", item, card: result.card, log: result.log };
      reviewed++;
      index++;
      revealed = false;
      hinted = false;
      draw();
    } finally {
      busy = false;
    }
  }

  /**
   * "I already know this" — retire the card rather than studying it.
   *
   * Exists because no word list can know what a given learner already owns. The
   * extractor has no corpus frequency data, so genuinely easy words do reach
   * decks; without an escape hatch they silently consume the daily new-card
   * budget, which is the fastest way to make someone abandon a study app.
   *
   * Does not increment `reviewed` — nothing was reviewed.
   */
  async function retire(item: QueueItem): Promise<void> {
    if (busy) return;
    busy = true;
    try {
      await suspendCard(item.card.id, item.direction);
      done.add(itemKey(item));
      undoPoint = { kind: "retire", item };
      index++;
      revealed = false;
      hinted = false;
      draw();
    } finally {
      busy = false;
    }
  }

  async function undo(): Promise<void> {
    if (!undoPoint || busy) return;
    busy = true;
    const point = undoPoint;
    try {
      if (point.kind === "review") {
        // ts-fsrs recomputes the pre-review state; the RPC deletes the log row and
        // restores it. Without this, a mis-tapped "didn't know" on a mature card
        // costs weeks of interval.
        const restored = rollback(point.card, point.log);
        await undoReview(point.item.card.id, point.item.direction, restored);
        reviewed = Math.max(0, reviewed - 1);
      } else {
        await unsuspendCard(point.item.card.id, point.item.direction);
      }
      done.delete(itemKey(point.item));
      undoPoint = null;
      index = Math.max(0, index - 1);
      revealed = false;
      hinted = false;
      draw();
    } finally {
      busy = false;
    }
  }

  function draw(): void {
    const item = queue[index];
    if (!item) return finish();

    shownAt = Date.now();
    const preview = previewIntervals(item.state, new Date());
    const now = new Date();

    /*
     * Which face shows what, per direction. 0 = EN→FA (recognition),
     * 1 = FA→EN (production).
     *
     * Everything supplementary — IPA, both example sentences, the general sense,
     * private notes — lands on the BACK regardless, because after the reveal it
     * is all just context. Only the prompt and the answer swap.
     */
    // Named *Text* rather than `prompt`/`answer`: `answer()` is the grading
    // function in this scope, and shadowing it here silently breaks both buttons.
    const reversed = item.direction === 1;
    const promptText = reversed ? item.card.back : item.card.front;
    const answerText = reversed ? item.card.front : item.card.back;

    /*
     * Hint: the example sentence in the PROMPT's language, never the answer's.
     *
     * Seeing a word in context usually triggers recall without handing over the
     * answer — a graduated prompt, which is stronger retrieval practice than
     * flipping straight to the translation. Which sentence is safe depends on the
     * direction: going EN→FA the English sentence reveals nothing, but going
     * FA→EN that same sentence contains the English word being asked for, so the
     * Persian one is the safe prompt instead.
     *
     * Using it does not change the grade. Two-button FSRS depends on honest
     * self-report, and auto-penalising a hint would both be paternalistic and
     * make people avoid a feature that helps them learn.
     */
    const hintText = reversed ? item.card.example_back : item.card.example;
    const canHint = !revealed && !!hintText;

    const face = h("div", { class: "card-face" },
      bidiText(promptText, "card-front"),
      // IPA transcribes the English, so on the way back it would spell out the
      // answer. It moves to the back instead.
      !reversed && item.card.ipa
        ? h("div", { class: "card-ipa muted", text: item.card.ipa, dir: "ltr" })
        : null,
      canHint && !hinted
        ? h("button", {
            class: "ghost hint-btn",
            text: `💡 ${t("hint")}`,
            on: {
              click: (event) => {
                event.stopPropagation(); // don't let the tap also flip the card
                hinted = true;
                draw();
              },
            },
          })
        : null,
      canHint && hinted ? bidiText(hintText!, "card-hint") : null,
    );

    const back = h("div", { class: "card-back", hidden: !revealed },
      bidiText(answerText, "card-answer"),
      reversed && item.card.ipa
        ? h("div", { class: "card-ipa muted", text: item.card.ipa, dir: "ltr" })
        : null,
      // The general sense comes after the deck's own sense, and only when the two
      // differ — that ordering matters, since the deck's sense is the one the
      // episode actually used and the reason this card exists.
      item.card.back_general
        ? h("div", { class: "sense" },
            h("span", { class: "sense-label muted small", text: t("senseGeneral") }),
            bidiText(item.card.back_general, "sense-text"),
          )
        : null,
      item.card.example ? bidiText(item.card.example, "card-example muted") : null,
      item.card.example_back ? bidiText(item.card.example_back, "card-example muted") : null,
      item.card.notes ? bidiText(item.card.notes, "card-example muted") : null,
    );

    const flipHint = h("div", { class: "muted small", text: t("tapToReveal"), hidden: revealed });

    const cardEl = h("div", {
      class: revealed ? "flashcard revealed" : "flashcard",
      attrs: { role: "button", tabindex: "0", "aria-label": t("tapToReveal") },
      on: {
        click: () => {
          if (!revealed) {
            revealed = true;
            draw();
          }
        },
      },
    }, face, back, flipHint);

    // The audio is always the English word, so going FA→EN it *is* the answer —
    // offering it before the reveal would just be a "tell me" button.
    const audio = !reversed || revealed
      ? h("button", {
          class: "ghost audio",
          text: "🔊",
          title: t("playAudio"),
          on: {
            click: (event) => {
              event.stopPropagation(); // don't also flip the card
              void playCard(item.card.audio_url, item.card.front, "en-US");
            },
          },
        })
      : null;

    const buttons = h("div", { class: "grade-row", hidden: !revealed },
      h("button", {
        class: "grade again",
        on: { click: () => void answer(item, "again") },
      },
        h("span", { text: t("didntKnow") }),
        h("span", { class: "grade-when", text: formatInterval(now, preview.again, lang()) }),
      ),
      h("button", {
        class: "grade good",
        on: { click: () => void answer(item, "good") },
      },
        h("span", { text: t("knewIt") }),
        h("span", { class: "grade-when", text: formatInterval(now, preview.good, lang()) }),
      ),
    );

    const progress = h("div", { class: "study-progress" },
      h("div", { class: "bar" },
        h("div", { class: "bar-fill", attrs: { style: `inline-size:${(index / queue.length) * 100}%` } }),
      ),
      h("span", { class: "muted small", text: `${num(index + 1)} / ${num(queue.length)}` }),
    );

    /*
     * Which side leads, as a pair where exactly one is always selected.
     *
     * Labelled by the card's sides rather than by language: a collection carries
     * its own `lang_front` / `lang_back`, so naming the languages would be wrong
     * for any deck that isn't English/Persian. No arrows either — "X → Y" between
     * two Persian words is mirrored by the RTL layout and contradicts itself.
     */
    const directionRow = h("div", { class: "segmented", attrs: { role: "group" } },
      ...([
        [0, t("coverFirst")],
        [1, t("backFirst")],
      ] as const).map(([value, label]) =>
        h("button", {
          class: currentDirection() === value ? "seg active" : "seg",
          text: label,
          attrs: { "aria-pressed": currentDirection() === value },
          on: { click: () => setDirection(value) },
        }),
      ),
    );

    mount(
      screen,
      progress,
      directionRow,
      cardEl,
      h("div", { class: "study-tools" },
        audio,
        // Deliberately available BEFORE reveal too: if you already know the word,
        // being forced to flip the card first defeats the purpose. Kept in the
        // tools row rather than beside the grade buttons so it can't be mis-tapped
        // while answering.
        h("button", {
          class: "ghost retire",
          text: t("alreadyKnow"),
          on: { click: () => void retire(item) },
        }),
        undoPoint
          ? h("button", { class: "ghost", text: t("undo"), on: { click: () => void undo() } })
          : null,
      ),
      buttons,
    );

    // Only the actions that are actually available right now are wired up, so a
    // drag toward something that would do nothing never tints or promises
    // anything — before the flip there is no grade to give.
    attachSwipe(cardEl, {
      left: revealed ? { label: t("didntKnow"), run: () => void answer(item, "again") } : null,
      right: revealed ? { label: t("knewIt"), run: () => void answer(item, "good") } : null,
      up: revealed
        ? null
        : { label: t("revealAnswer"), run: () => { revealed = true; draw(); } },
      down: { label: t("alreadyKnowShort"), run: () => void retire(item) },
    });
  }

  // Keyboard: space/enter to flip, then 1 = didn't know, 2/enter = knew it.
  const onKey = (event: KeyboardEvent) => {
    const item = queue[index];
    if (!item) return;
    if (!revealed && (event.key === " " || event.key === "Enter")) {
      event.preventDefault();
      revealed = true;
      draw();
      return;
    }
    if (event.key.toLowerCase() === "z") void undo();
    if (event.key.toLowerCase() === "k") void retire(item); // k = already know
    if (event.key.toLowerCase() === "r") setDirection(currentDirection() === 0 ? 1 : 0);
    // Must agree with `draw()` on which sentence is the safe prompt, or this
    // shortcut would offer a hint the card then refuses to show.
    const hintable = item.direction === 1 ? item.card.example_back : item.card.example;
    if (event.key.toLowerCase() === "h" && !revealed && hintable && !hinted) {
      hinted = true;
      draw();
    }
    if (revealed) {
      if (event.key === "1") void answer(item, "again");
      if (event.key === "2" || event.key === "Enter") void answer(item, "good");
    }
  };
  addEventListener("keydown", onKey);
  // The router replaces the outlet's children rather than tearing down listeners,
  // so remove this when the node leaves the document or every visit stacks another.
  new MutationObserver((_records, observer) => {
    if (!screen.isConnected) {
      removeEventListener("keydown", onKey);
      observer.disconnect();
    }
  }).observe(host, { childList: true });

  draw();
}

/** What a swipe in one direction does, or `null` when it isn't available now. */
type SwipeAction = { label: string; run: () => void } | null;

type SwipeDirection = "left" | "right" | "up" | "down";

const DIRECTIONS: readonly SwipeDirection[] = ["left", "right", "up", "down"];

/**
 * Swipe the card to act:
 *
 *   ← didn't know   → knew it   ↑ reveal   ↓ I already know this
 *
 * Faster than aiming at buttons one-handed, which is how this app is actually
 * used. `touchmove` is passive:false because a claimed drag must be able to
 * preventDefault, or the page moves along with the card.
 */
function attachSwipe(node: HTMLElement, actions: Record<SwipeDirection, SwipeAction>): void {
  const THRESHOLD = 60;
  /**
   * Down asks for a longer, more deliberate pull than the rest: it retires the
   * card, and a stray flick that costs you a word is worse than one that costs
   * you a grade. It is also the one direction a finger drifts toward naturally
   * while just holding the phone.
   */
  const DOWN_THRESHOLD = 110;

  const horizontal = !!(actions.left || actions.right);
  /*
   * Vertical is claimed only when nothing on the page has anywhere to scroll.
   * Owning an axis is all-or-nothing, so on a card too tall for the screen
   * taking it would leave the text unreadable — there the buttons stand in.
   * Decided here rather than in `touchstart` because the browser latches
   * `touch-action` at the moment the finger lands.
   */
  const vertical = !!(actions.up || actions.down) && !hasScrollRoom(node);
  if (!horizontal && !vertical) return;
  node.style.touchAction = vertical ? "none" : "pan-y";

  // Names the outcome mid-drag. Swiping down to destroy a card is not something
  // anyone would guess, and a tint alone doesn't say what it means.
  const badge = h("div", { class: "swipe-badge", hidden: true });
  node.appendChild(badge);

  let startX = 0;
  let startY = 0;
  /** Locked on the first decisive movement, so a lazy diagonal can't do two things. */
  let axis: "x" | "y" | null = null;
  let tracking = false;

  /** Which action releasing right now would run — null while under the threshold. */
  const intent = (dx: number, dy: number): SwipeDirection | null => {
    let direction: SwipeDirection | null = null;
    if (axis === "x") {
      if (Math.abs(dx) >= THRESHOLD) direction = dx > 0 ? "right" : "left";
    } else if (axis === "y") {
      if (dy <= -THRESHOLD) direction = "up";
      else if (dy >= DOWN_THRESHOLD) direction = "down";
    }
    return direction && actions[direction] ? direction : null;
  };

  node.addEventListener("touchstart", (event) => {
    const touch = event.touches[0];
    if (!touch) return;
    startX = touch.clientX;
    startY = touch.clientY;
    axis = null;
    tracking = true;
  }, { passive: true });

  node.addEventListener("touchmove", (event) => {
    if (!tracking) return;
    const touch = event.touches[0];
    if (!touch) return;
    const dx = touch.clientX - startX;
    const dy = touch.clientY - startY;

    if (!axis) {
      if (Math.abs(dx) < 10 && Math.abs(dy) < 10) return;
      axis = Math.abs(dx) > Math.abs(dy) ? "x" : "y";
      // Nothing on this axis: hand the touch back to the browser rather than
      // dragging a card that would do nothing on release.
      if (axis === "x" ? !horizontal : !vertical) {
        tracking = false;
        return;
      }
    }

    event.preventDefault();
    const offset = axis === "x" ? dx : dy;
    node.style.transform = axis === "x" ? `translateX(${dx}px)` : `translateY(${dy}px)`;
    node.style.opacity = String(Math.max(0.4, 1 - Math.abs(offset) / 400));

    // Tint and label only once past the threshold, so the drag doubles as a
    // preview of whether releasing now will actually register.
    const direction = intent(dx, dy);
    for (const name of DIRECTIONS) node.classList.toggle(`swipe-${name}`, name === direction);
    badge.textContent = direction ? actions[direction]!.label : "";
    badge.hidden = !direction;
  }, { passive: false });

  const reset = () => {
    node.style.transform = "";
    node.style.opacity = "";
    for (const name of DIRECTIONS) node.classList.remove(`swipe-${name}`);
    badge.hidden = true;
  };

  const end = (event: TouchEvent) => {
    if (!tracking) return;
    tracking = false;
    const touch = event.changedTouches[0];
    const direction = touch ? intent(touch.clientX - startX, touch.clientY - startY) : null;
    axis = null;
    reset();
    if (direction) actions[direction]!.run();
  };

  node.addEventListener("touchend", end, { passive: true });
  node.addEventListener("touchcancel", () => {
    tracking = false;
    axis = null;
    reset();
  }, { passive: true });
}

/** Does the nearest scrollable ancestor actually have anywhere left to scroll? */
function hasScrollRoom(node: HTMLElement): boolean {
  for (let el = node.parentElement; el; el = el.parentElement) {
    const overflow = getComputedStyle(el).overflowY;
    if (overflow === "auto" || overflow === "scroll") return el.scrollHeight > el.clientHeight + 1;
  }
  const root = document.documentElement;
  return root.scrollHeight > root.clientHeight + 1;
}
